import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaArrowRight,
  FaBabyCarriage,
  FaBars,
  FaChevronDown,
  FaFileInvoiceDollar,
  FaLaptop,
  FaLink,
  FaQrcode,
  FaTimes,
} from 'react-icons/fa';
import './Header.css';
import logo from '../../assets/logo/UpCo-Horizontal_ - line.png';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const location = useLocation();
  const isSolutionsRoute =
    location.pathname.startsWith('/solucoes') ||
    location.pathname.startsWith('/free-tools') ||
    location.pathname === '/free-solutions' ||
    location.pathname === '/custom-solutions';

  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      setIsSolutionsOpen(false);
    }
  };

  const toggleSolutions = () => {
    setIsSolutionsOpen(!isSolutionsOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsSolutionsOpen(false);
  };

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsSolutionsOpen(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsSolutionsOpen(false);
    }, 150);
  };

  useEffect(() => {
    setIsMenuOpen(false);
    setIsSolutionsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <header className="header">
        <div className="logo">
          <Link to="/" onClick={closeMenu} className="logo-link">
            <img src={logo} alt="UpdateCommit Logo" className="logo-img" />
          </Link>
        </div>

        <nav className="nav-menu">
          <ul>
            <li>
              <Link
                to="/"
                className={`nav-link ${location.pathname === "/" ? "active" : ""}`}
                onClick={closeMenu}
              >
                Início
              </Link>
            </li>
            <li
              className="solutions-menu"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                type="button"
                className={`solutions-link ${isSolutionsOpen || isSolutionsRoute ? 'active' : ''}`}
                aria-expanded={isSolutionsOpen}
                aria-haspopup="true"
                onClick={toggleSolutions}
              >
                Soluções
                <FaChevronDown className={`chevron ${isSolutionsOpen ? 'rotated' : ''}`} />
              </button>
              {isSolutionsOpen && (
                <div
                  className="solutions-dropdown"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="solutions-column">
                    <h4>Ferramentas Gratuitas</h4>
                    <div className="icon-grid">
                      <Link to="/free-tools/whatsapp-link-generator" className="icon-grid-item" onClick={closeMenu}>
                        <div className="icon-grid-box green"><FaLink /></div>
                        <span>WhatsApp</span>
                      </Link>
                      <Link to="/free-tools/qr-code-generator" className="icon-grid-item" onClick={closeMenu}>
                        <div className="icon-grid-box green"><FaQrcode /></div>
                        <span>QR Code</span>
                      </Link>
                      <Link to="/solucoes/baby" className="icon-grid-item" onClick={closeMenu}>
                        <div className="icon-grid-box green"><FaBabyCarriage /></div>
                        <span>Baby</span>
                      </Link>
                    </div>
                  </div>

                  <div className="solutions-column">
                    <h4>Soluções Avançadas</h4>
                    <div className="icon-grid">
                      <Link to="/solucoes/nfse" className="icon-grid-item" onClick={closeMenu}>
                        <div className="icon-grid-box purple"><FaFileInvoiceDollar /></div>
                        <span>NFS-e</span>
                        <span className="icon-grid-badge">Novo</span>
                      </Link>
                    </div>
                  </div>

                  <div className="solutions-column">
                    <h4>Sob Medida</h4>
                    <div className="icon-grid">
                      <Link to="/custom-solutions" className="icon-grid-item" onClick={closeMenu}>
                        <div className="icon-grid-box orange"><FaLaptop /></div>
                        <span>Dev Custom</span>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </li>
            <li>
              <Link
                to="/about"
                className={`nav-link ${location.pathname === "/about" ? "active" : ""}`}
                onClick={closeMenu}
              >
                Empresa
              </Link>
            </li>
          </ul>
        </nav>
        <a href="https://app.updatecommit.com"
          rel="noopener noreferrer"
          target="_blank"
          className="login-cta-button"
        >
          <span>Acessar plataforma</span>
          <FaArrowRight />
        </a>
        <button
          type="button"
          className={`hamburger ${isMenuOpen ? 'open' : ''}`}
          onClick={toggleMenu}
          aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={isMenuOpen}
        >
          <FaBars className="hamburger-icon" />
        </button>
      </header>

      <div className={`mobile-menu ${isMenuOpen ? "open" : ""}`}>
        <div className="mobile-header">
          <img src={logo} alt="UpdateCommit Logo" className="mobile-logo" />
          <button className="mobile-close" onClick={closeMenu} aria-label="Fechar menu">
            <FaTimes />
          </button>
        </div>
        <ul>
          <li>
            <Link
              to="/"
              className={`mobile-nav-link ${location.pathname === "/" ? "active" : ""}`}
              onClick={closeMenu}
            >
              Home
            </Link>
          </li>
          <li>
            <button
              type="button"
              onClick={toggleSolutions}
              aria-expanded={isSolutionsOpen}
              className={`mobile-solutions-link ${isSolutionsOpen ? 'active' : ''}`}
            >
              Soluções
              <FaChevronDown className={`mobile-chevron ${isSolutionsOpen ? 'rotated' : ''}`} />
            </button>
            <div className={`mobile-submenu ${isSolutionsOpen ? 'open' : ''}`}>
              {/* Wrapper obrigatório: grid-template-rows 0fr→1fr anima a
                  altura real do conteúdo, sem max-height chutado. */}
              <div className="mobile-submenu-inner">
              <div className="mobile-section">
                <h5>Ferramentas Gratuitas</h5>
                <Link to="/free-tools/whatsapp-link-generator" onClick={closeMenu}>
                  <FaLink /> Link WhatsApp
                </Link>
                <Link to="/free-tools/qr-code-generator" onClick={closeMenu}>
                  <FaQrcode /> Gerador QR Code
                </Link>
                <Link to="/solucoes/baby" onClick={closeMenu}>
                  <FaBabyCarriage /> UpdateCommit Baby
                </Link>
              </div>
              <div className="mobile-section">
                <h5>Soluções Avançadas</h5>
                <Link to="/solucoes/nfse" onClick={closeMenu}>
                  <FaFileInvoiceDollar /> Emissor de NFS-e
                </Link>
              </div>
              <div className="mobile-section">
                <h5>Sob Medida</h5>
                <Link to="/custom-solutions" onClick={closeMenu}>
                  <FaLaptop /> Desenvolvimento Custom
                </Link>
              </div>
              </div>
            </div>
          </li>
          <li>
            <Link
              to="/about"
              className={`mobile-nav-link ${location.pathname === "/about" ? "active" : ""}`}
              onClick={closeMenu}
            >
              Sobre
            </Link>
          </li>
        </ul>
        <div className="mobile-cta-container">
          <a
            href="https://app.updatecommit.com"
            rel="noopener noreferrer"
            target="_blank"
            className="mobile-login-cta"
            onClick={closeMenu}
          >
            <span>Acessar plataforma</span>
            <FaArrowRight />
          </a>
        </div>
      </div>

      {isMenuOpen && <div className="backdrop" onClick={closeMenu}></div>}
    </>
  );
};

export default Header;
